import type { FormWithProcesses } from '@/api/processStepsApi'

/**
 * Which forms embed each process field (z8nrz7cnj6).
 *
 * Inverts `GET /process-steps/forms`, which the Process page already loads to
 * populate its form filter. Inverting what is in hand rather than calling
 * `GET /field-library/:id/usage` per row keeps the page to one request, and —
 * more importantly — means the "Forms" column and the form filter cannot
 * disagree, because both read the same list.
 *
 * Why the count is worth showing at all: process config is keyed on the LIBRARY
 * field, not the placed copy, so every form embedding the same process shares
 * one bill of materials. Editing a process for "the collection form" silently
 * edits it for every other form using it. This column is where that stops being
 * a surprise.
 *
 * Order is preserved from the source, which the API sorts by name then version
 * descending — so a form's newest version leads, and two versions of one form
 * sit together rather than scattered.
 */
export function formsByProcessField(
  forms: FormWithProcesses[],
): Map<string, FormWithProcesses[]> {
  const byField = new Map<string, FormWithProcesses[]>()
  for (const form of forms) {
    // A form embedding the same process twice must not be listed twice: the
    // question is which FORMS use it, not how many places within them.
    for (const fieldId of new Set(form.fieldIds)) {
      const held = byField.get(fieldId)
      if (held) held.push(form)
      else byField.set(fieldId, [form])
    }
  }
  return byField
}

/**
 * One entry per form FAMILY, holding the newest version of each — insertion
 * order preserved, so first appearance decides position.
 *
 * `GET /process-steps/forms` returns a row per form DEFINITION, and versions are
 * definitions: four versions of "kit creation form" are four rows. Offered to a
 * user unchanged, one form fills a dropdown four times with nothing to choose
 * between the entries, which is the duplication people report as duplicate
 * processes.
 *
 * Keyed on `familyId`, NOT on the name. Name-keying looks equivalent — versions
 * of one form do share a name — but it also fuses two unrelated forms that
 * happen to be called the same thing, and the schema explicitly allows that
 * (which is why version lineage is keyed on familyId in the first place). The
 * consequence is not a cosmetic one: the loser of the fusion disappears from the
 * list, and every process only IT embeds looks like it belongs to no form.
 *
 * Versions are compared rather than taking the first arrival: the API's
 * name-then-version-descending sort makes those agree today, but a pure function
 * that depends on an upstream sort it cannot see is a trap for the next caller.
 */
export function newestPerFamily(forms: FormWithProcesses[]): FormWithProcesses[] {
  const newest = new Map<string, FormWithProcesses>()
  for (const form of forms) {
    const held = newest.get(form.familyId)
    if (!held || (form.version ?? 1) > (held.version ?? 1)) newest.set(form.familyId, form)
  }
  return [...newest.values()]
}

/** A form, collapsed across its versions, with every process any version embeds. */
export interface FormFamilyWithProcesses {
  /** What "one form" means here — the lineage, not a definition. */
  familyId: string
  /** The newest version's definition id, for callers needing a concrete form. */
  id: string
  name: string
  /** The newest version. */
  version: number | null
  /** Process keys embedded by ANY version of the family, first-seen order. */
  fieldIds: string[]
}

/**
 * The forms to OFFER as a filter — one per family, each carrying every process
 * any of its versions embeds.
 *
 * The union across versions is the deliberate part. A process dropped from the
 * newest version is still that form's process: its older versions' submissions
 * resolve configuration through the same library row, so the row is neither
 * orphaned nor safe to treat as unattached. Scoping to the newest version alone
 * would drop it out of every form's list and into "Not in any form", which is
 * the same wrong answer the backend's `LIKE`-based dedupe used to give — and
 * here it would be a filter that hides a live process rather than a cleanup that
 * spares a dead one.
 */
export function formFamiliesWithProcesses(
  forms: FormWithProcesses[],
): FormFamilyWithProcesses[] {
  const byFamily = new Map<string, FormFamilyWithProcesses>()
  for (const form of forms) {
    const held = byFamily.get(form.familyId)
    if (!held) {
      byFamily.set(form.familyId, {
        familyId: form.familyId,
        id: form.id,
        name: form.name,
        version: form.version,
        fieldIds: [...new Set(form.fieldIds)],
      })
      continue
    }
    for (const fieldId of form.fieldIds) {
      if (!held.fieldIds.includes(fieldId)) held.fieldIds.push(fieldId)
    }
    // The family is named and numbered by its newest version, whichever order
    // the versions arrived in.
    if ((form.version ?? 1) > (held.version ?? 1)) {
      held.id = form.id
      held.name = form.name
      held.version = form.version
    }
  }
  return [...byFamily.values()]
}

/** One form family that embeds a given process field. */
export interface EmbeddingFormFamily {
  familyId: string
  name: string
  /** The newest version of this family that embeds the field. */
  version: number | null
  /** How many of the family's versions embed it — always at least 1. */
  versionCount: number
}

/**
 * The definitions embedding ONE process field, collapsed to forms (z8nrz7cnj6).
 *
 * Feed it a `formsByProcessField` entry. The uncollapsed list counts definitions,
 * so a process living in Collection V2 and V3 reads as "2 forms". Nobody means
 * that by "forms": it is one form, and what the Forms column is asked for is how
 * many forms share this bill of materials. `versionCount` keeps the detail that
 * was there — a row can still name the newest version and say how many hold it —
 * without the count itself lying.
 */
export function embeddingFamilies(forms: FormWithProcesses[]): EmbeddingFormFamily[] {
  const families = new Map<string, EmbeddingFormFamily>()
  for (const form of forms) {
    const held = families.get(form.familyId)
    if (!held) {
      families.set(form.familyId, {
        familyId: form.familyId,
        name: form.name,
        version: form.version,
        versionCount: 1,
      })
      continue
    }
    held.versionCount += 1
    if ((form.version ?? 1) > (held.version ?? 1)) {
      held.name = form.name
      held.version = form.version
    }
  }
  return [...families.values()]
}
